import React, { useMemo } from 'react';
import {
  calculateHRZonesFromMax,
  calculatePowerZonesFromFTP,
  estimateFTPFromMaxPower,
} from '../utils/zoneCalculator';
import './RideStatsPanel.css';

function formatDuration(seconds) {
  const s = Math.max(0, Math.round(seconds || 0));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (hh > 0) return `${hh}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  return `${mm}:${String(ss).padStart(2, '0')}`;
}

function formatNumber(value, digits = 1) {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function getValidNumber(v) {
  const n = typeof v === 'string' ? Number(v) : v;
  return typeof n === 'number' && Number.isFinite(n) ? n : null;
}

function getFiniteMinMax(values) {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < values.length; i += 1) {
    const n = getValidNumber(values[i]);
    if (n === null) continue;
    if (n < min) min = n;
    if (n > max) max = n;
  }
  if (min === Infinity || max === -Infinity) return null;
  return { min, max };
}

function getTimeArray(telemetryData) {
  return telemetryData
    .map((d) => getValidNumber(d.time))
    .filter((t) => t !== null)
    .sort((a, b) => a - b);
}

function computeDurationSeconds(telemetryData, fallbackDuration) {
  const times = getTimeArray(telemetryData);
  if (times.length >= 2) return Math.max(0, times[times.length - 1] - times[0]);
  return Math.max(0, fallbackDuration || 0);
}

function computeDistanceKm(telemetryData) {
  if (!telemetryData || telemetryData.length < 2) return null;

  const distanceValues = telemetryData
    .map((d) => getValidNumber(d.distance))
    .filter((v) => v !== null);

  if (distanceValues.length >= 2) {
    const distanceRange = getFiniteMinMax(distanceValues);
    if (!distanceRange) return null;
    const { min: minD, max: maxD } = distanceRange;
    const raw = Math.max(0, maxD - minD);
    // Heuristic: CSV often stores meters; FIT may store km. Treat >1000 as meters.
    const km = maxD > 1000 ? raw / 1000 : raw;
    return km;
  }

  const hasGps = telemetryData.some((d) => getValidNumber(d.lat) !== null && getValidNumber(d.lon) !== null);
  if (hasGps) {
    let totalKm = 0;
    let prev = null;
    for (const d of telemetryData) {
      const lat = getValidNumber(d.lat);
      const lon = getValidNumber(d.lon);
      if (lat === null || lon === null) continue;
      if (prev) {
        totalKm += haversineKm(prev.lat, prev.lon, lat, lon);
      }
      prev = { lat, lon };
    }
    return totalKm;
  }

  // Fallback: integrate speed over time (speed assumed km/h)
  const points = telemetryData
    .map((d) => ({ t: getValidNumber(d.time), s: getValidNumber(d.speed) }))
    .filter((p) => p.t !== null && p.s !== null)
    .sort((a, b) => a.t - b.t);

  if (points.length < 2) return null;

  let km = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const dt = points[i + 1].t - points[i].t;
    if (!Number.isFinite(dt) || dt <= 0) continue;
    const s1 = points[i].s;
    const s2 = points[i + 1].s;
    const avgKmh = (s1 + s2) / 2;
    km += (avgKmh * dt) / 3600;
  }
  return km;
}

function timeWeightedAvg(telemetryData, key) {
  const points = telemetryData
    .map((d) => ({ t: getValidNumber(d.time), v: getValidNumber(d[key]) }))
    .filter((p) => p.t !== null && p.v !== null && p.v > 0)
    .sort((a, b) => a.t - b.t);
  if (points.length < 2) return null;

  let sum = 0;
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const dt = points[i + 1].t - points[i].t;
    if (!Number.isFinite(dt) || dt <= 0) continue;
    sum += points[i].v * dt;
    total += dt;
  }
  if (total <= 0) return null;
  return sum / total;
}

function maxOf(telemetryData, key) {
  const values = telemetryData.map((d) => getValidNumber(d[key])).filter((v) => v !== null && v > 0);
  if (!values.length) return null;
  const range = getFiniteMinMax(values);
  return range ? range.max : null;
}

function elevationSummary(telemetryData) {
  const values = telemetryData.map((d) => getValidNumber(d.elevation)).filter((v) => v !== null);
  if (values.length < 2) return null;
  const range = getFiniteMinMax(values);
  if (!range) return null;
  const { min, max } = range;

  let gain = 0;
  let loss = 0;
  let prev = null;
  const noiseThresholdM = 0.5;
  for (const v of values) {
    if (prev === null) {
      prev = v;
      continue;
    }
    const diff = v - prev;
    if (diff > noiseThresholdM) gain += diff;
    if (diff < -noiseThresholdM) loss += -diff;
    prev = v;
  }
  return { min, max, gain, loss };
}

function extractZoneRanges(trainingZones) {
  if (!trainingZones) return { hrZones: null, powerZones: null };
  // Stored shape is usually { hrZones, powerZones } but some helpers return zone1..zone5 directly.
  const hrZones = trainingZones.hrZones || (trainingZones.zone1 ? trainingZones : null);
  const powerZones = trainingZones.powerZones || null;
  return { hrZones, powerZones };
}

function getZoneIndex(zones, value) {
  if (!zones || value === null || value === undefined) return null;
  for (let z = 1; z <= 5; z++) {
    const r = zones[`zone${z}`];
    if (!r) continue;
    const min = getValidNumber(r.min);
    const max = getValidNumber(r.max);
    if (min === null || max === null) continue;
    if (z === 5) {
      if (value >= min && value <= max) return z;
    } else {
      if (value >= min && value < max) return z;
    }
  }
  return null;
}

function computeZoneDistributionSeconds(telemetryData, key, zones) {
  const points = telemetryData
    .map((d) => ({ t: getValidNumber(d.time), v: getValidNumber(d[key]) }))
    .filter((p) => p.t !== null && p.v !== null && p.v > 0)
    .sort((a, b) => a.t - b.t);
  if (points.length < 2 || !zones) return null;

  const zoneSeconds = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let total = 0;
  for (let i = 0; i < points.length - 1; i++) {
    const dt = points[i + 1].t - points[i].t;
    if (!Number.isFinite(dt) || dt <= 0) continue;
    const zi = getZoneIndex(zones, points[i].v);
    if (zi) zoneSeconds[zi] += dt;
    total += dt;
  }
  if (total <= 0) return null;
  return { zoneSeconds, totalSeconds: total };
}

function formatZoneRange(zones, zone, unit) {
  if (!zones || !zones[`zone${zone}`]) return '';
  const { min, max } = zones[`zone${zone}`];
  if (min === undefined || max === undefined) return '';
  return `${min}-${max}${unit}`;
}

export default function RideStatsPanel({
  telemetryData = [],
  duration: durationProp = 0,
  trainingZones = null,
}) {
  const stats = useMemo(() => {
    const durationSeconds = computeDurationSeconds(telemetryData, durationProp);

    const distanceKm = computeDistanceKm(telemetryData);
    const maxSpeed = maxOf(telemetryData, 'speed'); // assumed km/h

    const avgSpeed = distanceKm && durationSeconds > 0 ? (distanceKm / durationSeconds) * 3600 : null;

    const avgHR = timeWeightedAvg(telemetryData, 'heartRate');
    const maxHR = maxOf(telemetryData, 'heartRate');

    const avgPower = timeWeightedAvg(telemetryData, 'power');
    const maxPower = maxOf(telemetryData, 'power');

    const elev = elevationSummary(telemetryData);

    const { hrZones: configuredHrZones, powerZones: configuredPowerZones } = extractZoneRanges(trainingZones);

    const hrZones = configuredHrZones || (maxHR ? calculateHRZonesFromMax(Math.round(maxHR)) : null);
    const powerZones =
      configuredPowerZones ||
      (() => {
        if (!maxPower) return null;
        const ftp = estimateFTPFromMaxPower(maxPower);
        return ftp ? calculatePowerZonesFromFTP(ftp) : null;
      })();

    const hrZoneDist = hrZones ? computeZoneDistributionSeconds(telemetryData, 'heartRate', hrZones) : null;
    const powerZoneDist = powerZones ? computeZoneDistributionSeconds(telemetryData, 'power', powerZones) : null;

    return {
      durationSeconds,
      distanceKm,
      maxSpeed,
      avgSpeed,
      avgHR,
      maxHR,
      avgPower,
      maxPower,
      elev,
      hrZones,
      powerZones,
      hrZoneDist,
      powerZoneDist,
      zonesAreEstimated: !configuredHrZones && !configuredPowerZones,
    };
  }, [telemetryData, durationProp, trainingZones]);

  const hasHR = telemetryData.some((d) => (getValidNumber(d.heartRate) || 0) > 0);
  const hasPower = telemetryData.some((d) => (getValidNumber(d.power) || 0) > 0);
  const hasElevation = telemetryData.some((d) => getValidNumber(d.elevation) !== null);

  return (
    <div className="ride-stats-panel">
      <div className="ride-stats-header">
        <div className="ride-stats-title">Ride stats</div>
        {stats.zonesAreEstimated && (hasHR || hasPower) && (
          <div className="ride-stats-subtitle">Zones estimated from ride data (no zone config found).</div>
        )}
      </div>

      <div className="ride-stats-grid">
        <div className="ride-stat">
          <div className="ride-stat-label">Duration</div>
          <div className="ride-stat-value">{formatDuration(stats.durationSeconds)}</div>
        </div>
        <div className="ride-stat">
          <div className="ride-stat-label">Distance</div>
          <div className="ride-stat-value">{stats.distanceKm === null ? '—' : `${formatNumber(stats.distanceKm, 2)} km`}</div>
        </div>
        <div className="ride-stat">
          <div className="ride-stat-label">Avg speed</div>
          <div className="ride-stat-value">{stats.avgSpeed === null ? '—' : `${formatNumber(stats.avgSpeed, 1)} km/h`}</div>
        </div>
        <div className="ride-stat">
          <div className="ride-stat-label">Max speed</div>
          <div className="ride-stat-value">{stats.maxSpeed === null ? '—' : `${formatNumber(stats.maxSpeed, 1)} km/h`}</div>
        </div>

        {hasHR && (
          <>
            <div className="ride-stat">
              <div className="ride-stat-label">Avg HR</div>
              <div className="ride-stat-value">{stats.avgHR === null ? '—' : `${formatNumber(stats.avgHR, 0)} bpm`}</div>
            </div>
            <div className="ride-stat">
              <div className="ride-stat-label">Max HR</div>
              <div className="ride-stat-value">{stats.maxHR === null ? '—' : `${formatNumber(stats.maxHR, 0)} bpm`}</div>
            </div>
          </>
        )}

        {hasPower && (
          <>
            <div className="ride-stat">
              <div className="ride-stat-label">Avg power</div>
              <div className="ride-stat-value">{stats.avgPower === null ? '—' : `${formatNumber(stats.avgPower, 0)} W`}</div>
            </div>
            <div className="ride-stat">
              <div className="ride-stat-label">Max power</div>
              <div className="ride-stat-value">{stats.maxPower === null ? '—' : `${formatNumber(stats.maxPower, 0)} W`}</div>
            </div>
          </>
        )}

        {hasElevation && stats.elev && (
          <>
            <div className="ride-stat">
              <div className="ride-stat-label">Elevation min/max</div>
              <div className="ride-stat-value">
                {formatNumber(stats.elev.min, 0)} / {formatNumber(stats.elev.max, 0)} m
              </div>
            </div>
            <div className="ride-stat">
              <div className="ride-stat-label">Elevation gain/loss</div>
              <div className="ride-stat-value">
                +{formatNumber(stats.elev.gain, 0)} / -{formatNumber(stats.elev.loss, 0)} m
              </div>
            </div>
          </>
        )}
      </div>

      {(hasHR || hasPower) && (
        <div className="ride-zones">
          {hasHR && stats.hrZoneDist && (
            <div className="ride-zone-block">
              <div className="ride-zone-title">Heart rate zones</div>
              <div className="ride-zone-list">
                {[1, 2, 3, 4, 5].map((z) => {
                  const seconds = stats.hrZoneDist.zoneSeconds[z] || 0;
                  const pct = stats.hrZoneDist.totalSeconds > 0 ? (seconds / stats.hrZoneDist.totalSeconds) * 100 : 0;
                  return (
                    <div key={`hr-${z}`} className="ride-zone-row">
                      <div className="ride-zone-name">
                        Z{z} <span className="ride-zone-range">{formatZoneRange(stats.hrZones, z, ' bpm')}</span>
                      </div>
                      <div className="ride-zone-bar">
                        <div className="ride-zone-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="ride-zone-value">
                        {formatDuration(seconds)} ({formatNumber(pct, 0)}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {hasPower && stats.powerZoneDist && (
            <div className="ride-zone-block">
              <div className="ride-zone-title">Power zones</div>
              <div className="ride-zone-list">
                {[1, 2, 3, 4, 5].map((z) => {
                  const seconds = stats.powerZoneDist.zoneSeconds[z] || 0;
                  const pct = stats.powerZoneDist.totalSeconds > 0 ? (seconds / stats.powerZoneDist.totalSeconds) * 100 : 0;
                  return (
                    <div key={`pwr-${z}`} className="ride-zone-row">
                      <div className="ride-zone-name">
                        Z{z} <span className="ride-zone-range">{formatZoneRange(stats.powerZones, z, ' W')}</span>
                      </div>
                      <div className="ride-zone-bar">
                        <div className="ride-zone-bar-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <div className="ride-zone-value">
                        {formatDuration(seconds)} ({formatNumber(pct, 0)}%)
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

